import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { notificationsUnreadCount } from "../lib/api";
import { LayoutDashboard, UserPlus, UserMinus, Users, UsersRound, Printer, GraduationCap, Menu, LogOut, FileSpreadsheet, ShieldCheck, ClipboardCheck, BarChart3, ArrowUpCircle, BookOpenCheck, MessageCircle, MessagesSquare, Megaphone, FileText } from "lucide-react";
import { SCHOOL_LOGO, SCHOOL_NAME, SCHOOL_SUB, ROLES, can } from "../lib/constants";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import StageSwitcher from "./StageSwitcher";

// link.show(role): function returns true if link should appear for that role
const ALL_LINKS = [
  { to: "/", label: "بوابة الأنظمة", icon: LayoutDashboard, testid: "nav-portal", show: () => true, exact: true, external: true },
  { to: "/students", label: "اللوحة الرئيسية", icon: LayoutDashboard, testid: "nav-dashboard", show: () => true },
  { to: "/new/register", label: "تسجيل طالب مستجد", icon: UserPlus, testid: "nav-new-register", show: (u) => can.createStudent(u) },
  { to: "/new", label: "عرض المستجدين", icon: Users, testid: "nav-new-list", show: () => true },
  { to: "/withdrawn/register", label: "تسجيل طالب منسحب", icon: UserMinus, testid: "nav-withdrawn-register", show: (u) => can.createStudent(u) },
  { to: "/withdrawn", label: "عرض المنسحبين", icon: UsersRound, testid: "nav-withdrawn-list", show: () => true },
  { to: "/attendance", label: "تسجيل الغياب", icon: ClipboardCheck, testid: "nav-attendance", show: (u) => can.takeAttendance(u) },
  { to: "/attendance/reports", label: "تقارير الغياب", icon: BarChart3, testid: "nav-attendance-reports", show: (u) => can.viewAttendanceReports(u) },
  { to: "/lesson-plans", label: "الخطط الدراسية", icon: BookOpenCheck, testid: "nav-lesson-plans", show: (u) => can.viewLessonPlans(u) },
  { to: "/pickup/teacher", label: "استدعاء الطالب", icon: Megaphone, testid: "nav-pickup", show: (u) => can.viewPickup(u) },
  { to: "/pickup/admin", label: "إدارة الاستدعاءات", icon: Megaphone, testid: "nav-pickup-admin", show: (u) => can.managePickup(u) || can.manageGuardians(u) || can.manageSettingsPickup(u) },
  { to: "/pickup/duty", label: "المناوب", icon: Megaphone, testid: "nav-pickup-duty", show: (u) => can.dutyCallPickup(u) },
  { to: "/circulars", label: "التعاميم الداخلية", icon: FileText, testid: "nav-circulars", show: (u) => can.issueCirculars(u) || can.viewSignCirculars(u) },
  { to: "/whatsapp/compose", label: "إرسال إشعار غياب", icon: MessageCircle, testid: "nav-whatsapp-compose", show: (u) => can.sendWhatsapp(u) },
  { to: "/whatsapp/logs", label: "سجل الإشعارات", icon: MessagesSquare, testid: "nav-whatsapp-logs", show: (u) => can.viewWhatsappLog(u) },
  { to: "/reports", label: "تقارير الطلاب", icon: Printer, testid: "nav-reports", show: (u) => can.viewReports(u) },
  { to: "/import", label: "استيراد من Excel", icon: FileSpreadsheet, testid: "nav-import", show: (u) => can.importStudents(u) },
  { to: "/promote", label: "الترقية الجماعية", icon: ArrowUpCircle, testid: "nav-promote", show: (u) => can.promoteStudents(u) },
  { to: "/users", label: "إدارة المستخدمين", icon: ShieldCheck, testid: "nav-users", show: (u) => can.manageUsers(u) },
];

function SidebarBody({ onNavigate }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const links = ALL_LINKS.filter(l => l.show(user));
  const [unreadCirculars, setUnreadCirculars] = useState(0);

  useEffect(() => {
    if (!(can.issueCirculars(user) || can.viewSignCirculars(user))) return;
    const load = () => {
      notificationsUnreadCount().then((d) => setUnreadCirculars(d.count)).catch(() => {});
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [user]);

  const onLogout = async () => {
    if (onNavigate) onNavigate();
    // Navigate to portal FIRST so ProtectedRoute doesn't grab a null user
    // and redirect to /login while `logout()` is in flight (race condition).
    nav("/", { replace: true });
    await logout();
  };

  return (
    <>
      <div className="p-2.5 sm:p-3 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-teal-50 ring-1 ring-teal-200/60 flex items-center justify-center overflow-hidden shrink-0">
            <img src={SCHOOL_LOGO} alt="logo" className="w-7 h-7 object-contain" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-cairo text-[13px] font-extrabold text-teal-800 leading-tight truncate">{SCHOOL_NAME}</div>
          </div>
          {user && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-teal-700 text-white text-[9px] font-cairo whitespace-nowrap shrink-0">
              {ROLES[user.role]}
            </span>
          )}
        </div>
        {user && (
          <div className="text-[11px] text-slate-500 mt-1 truncate" data-testid="current-user-name">
            مرحباً، <span className="font-bold text-teal-800">{user.full_name || user.username}</span>
          </div>
        )}
      </div>

      <StageSwitcher />

      <nav className="flex-1 overflow-y-auto py-3 px-3">
        <div className="text-[11px] text-slate-400 px-3 py-2 font-cairo tracking-wider">القائمة الرئيسية</div>
        <ul className="space-y-1">
          {links.map(({ to, label, icon: Icon, testid, exact, external }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={exact || to === "/students"}
                onClick={onNavigate}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 group ${
                    isActive && !external ? "bg-teal-700 text-white shadow-sm shadow-teal-700/20" : external ? "text-teal-800 bg-teal-50/50 hover:bg-teal-100 border border-teal-100" : "text-slate-700 hover:bg-teal-50 hover:text-teal-800"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={18} className={isActive && !external ? "text-white" : "text-teal-700"} />
                    <span className="font-medium flex-1">{label}</span>
                    {to === "/circulars" && unreadCirculars > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold">
                        {unreadCirculars}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="px-3 mt-4">
          <Button variant="outline" onClick={onLogout} className="w-full justify-start gap-2 h-10 text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="btn-logout">
            <LogOut size={16} /> تسجيل الخروج
          </Button>
        </div>
      </nav>

      <div className="p-3 border-t border-slate-100 space-y-2" data-testid="sidebar-credits">
        <div className="rounded-lg bg-gradient-to-l from-amber-50/70 to-teal-50 border border-amber-200/60 px-3 py-2.5">
          <div className="text-[12px] text-slate-500">مدير المدرسة</div>
          <div className="font-cairo font-bold text-[15px] text-teal-800 leading-tight">محمد عبدالله الصبحي</div>
        </div>
        <div className="rounded-lg bg-gradient-to-l from-teal-50 to-amber-50/60 border border-teal-100 px-3 py-2.5">
          <div className="text-[12px] text-slate-500">برمجة وتصميم</div>
          <div className="font-cairo font-bold text-[15px] text-teal-800 leading-tight">محمد كامل</div>
          <a href="tel:0590939015" className="block mt-1 text-[13px] text-slate-600 hover:text-teal-700 tabular-nums font-medium" dir="ltr">0590939015</a>
        </div>
      </div>
    </>
  );
}

export default function Sidebar() {
  return (
    <aside className="no-print fixed top-0 right-0 h-screen w-72 bg-white border-l border-slate-200 hidden lg:flex flex-col" data-testid="sidebar">
      <SidebarBody />
    </aside>
  );
}

export function MobileTopbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="no-print lg:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200 px-3 py-2.5 flex items-center justify-between" data-testid="mobile-topbar">
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-teal-50 ring-1 ring-teal-200/60 flex items-center justify-center overflow-hidden shrink-0">
          <img src={SCHOOL_LOGO} alt="logo" className="w-8 h-8 object-contain" />
        </div>
        <div className="min-w-0">
          <div className="font-cairo text-[13px] font-extrabold text-teal-800 leading-tight truncate">{SCHOOL_NAME}</div>
          <div className="text-[10px] text-slate-500 truncate">نظام إدارة الطلاب</div>
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="text-teal-700 hover:bg-teal-50" data-testid="mobile-menu-btn" aria-label="القائمة">
            <Menu size={22} />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-72 p-0 flex flex-col" dir="rtl" data-testid="mobile-sidebar">
          <SidebarBody onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    </header>
  );
}
